import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Play, Swords, Trophy, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { matchRepo } from '@/db/repositories/matchRepo';
import { useHistoryStore } from '@/stores/historyStore';
import { useToast } from '@/hooks/use-toast';
import { useHeaderAction } from '@/contexts/HeaderActionContext';
import { formatDate } from '@/lib/utils';
import type { Match } from '@/types';
import { MATCH_FORMAT_CONFIG, GAME_MODE_CONFIG } from '@/types';

export function MatchesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setHeaderAction } = useHeaderAction();
  const [activeMatches, setActiveMatches] = useState<Match[]>([]);
  const [isLoadingActive, setIsLoadingActive] = useState(true);

  const {
    completedMatches,
    isLoading: isLoadingCompleted,
    error,
    loadCompletedMatches,
    clearError,
  } = useHistoryStore();

  useEffect(() => {
    async function loadActiveMatches() {
      try {
        const matches = await matchRepo.getActive();
        matches.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setActiveMatches(matches);
      } catch (err) {
        console.error('Failed to load active matches:', err);
      } finally {
        setIsLoadingActive(false);
      }
    }
    loadActiveMatches();
    loadCompletedMatches();
  }, [loadCompletedMatches]);

  useEffect(() => {
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error,
      });
      clearError();
    }
  }, [error, toast, clearError]);

  useEffect(() => {
    setHeaderAction(
      <Button
        size="icon"
        onClick={() => navigate('/matches/new')}
      >
        <Plus className="h-5 w-5" />
      </Button>
    );
    return () => setHeaderAction(null);
  }, [setHeaderAction, navigate]);

  const isLoading = isLoadingActive || isLoadingCompleted;

  if (isLoading) {
    return (
      <div className="container py-6">
        <div className="flex items-center justify-center h-40">
          <p className="text-muted-foreground">Loading matches...</p>
        </div>
      </div>
    );
  }

  // Group completed matches by date
  const matchesByDate = completedMatches.reduce(
    (groups, match) => {
      const date = match.completedAt
        ? formatDate(new Date(match.completedAt))
        : 'Unknown';
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(match);
      return groups;
    },
    {} as Record<string, typeof completedMatches>
  );

  return (
    <div className="container py-6 space-y-6 pb-24">
      {/* Active Matches Section */}
      <div className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Play className="h-4 w-4" />
          Active Matches ({activeMatches.length})
        </h2>

        {activeMatches.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center">
              <Swords className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                No active matches. Start a new match to begin playing!
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {activeMatches.map((match) => (
              <Card
                key={match.id}
                className="cursor-pointer hover:bg-accent/50 transition-colors border-primary/50"
                onClick={() => navigate(`/matches/active/${match.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium">
                          {MATCH_FORMAT_CONFIG[match.format].label}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {GAME_MODE_CONFIG[match.mode].label}
                        </span>
                      </div>
                      <p className="text-sm truncate">
                        {match.participants.map((p) => p.name).join(' vs ')}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Started {formatDate(new Date(match.createdAt))}
                      </p>
                    </div>
                    <Button size="sm" variant="ghost">
                      <Play className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Completed Matches Section */}
      <div className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Trophy className="h-4 w-4" />
          Completed Matches ({completedMatches.length})
        </h2>

        {completedMatches.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center">
              <p className="text-sm text-muted-foreground">
                No completed matches yet. Finish a match to see your history!
              </p>
            </CardContent>
          </Card>
        ) : (
          Object.entries(matchesByDate).map(([date, matches]) => (
            <div key={date} className="space-y-3">
              <h3 className="text-xs text-muted-foreground flex items-center gap-2 pl-1">
                <Calendar className="h-3 w-3" />
                {date}
              </h3>
              <div className="space-y-2">
                {matches.map((match) => {
                  const winner = match.participants.find(
                    (p) => p.id === match.winnerId
                  );
                  const formatConfig = MATCH_FORMAT_CONFIG[match.format];
                  return (
                    <Card
                      key={match.id}
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => navigate(`/matches/${match.id}`)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{formatConfig.label}</p>
                            <p className="text-sm text-muted-foreground">
                              {match.participants.map((p) => p.name).join(' vs ')}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {GAME_MODE_CONFIG[match.mode].label} - Target:{' '}
                              {match.targetScore}
                            </p>
                          </div>
                          <div className="text-right">
                            {winner && (
                              <div className="flex items-center gap-1">
                                <Trophy className="h-4 w-4 text-yellow-500" />
                                <span className="font-medium">{winner.name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
